// THE DRIVER — a hook inside MapWindow (the one component with access to layers, samples and the
// map stores) that RECONCILES the platform to the current step's scene, idempotently: it only acts
// when actual state differs from the scene, so it converges and cannot loop.
import { create } from "zustand";

/* * how a split side / solo layer is described in a scene — resolved by the driver at run time */
export type WizardLayerSpec =
  | { kind: "samples"; prop: string }
  | { kind: "id"; id: string }
  | {
      kind: "covariate";
      match: string; /* case-insensitive regex source over the covariate title */
    };

export interface WizardScene {
  /*
   * which surface the step lives on: 'dashboard' steps survive (and require) the map CLOSED
   * absent = 'map' (the tour's original scope)
   */
  view?: "map" | "dashboard";
  /* * the sidebar-picked layer soloed onto the MAIN map (no split) */
  solo?: WizardLayerSpec;
  /* * split view: null = ensure the split is OFF; a spec = ensure it is on with these sides */
  split?: {
    left: WizardLayerSpec;
    right: WizardLayerSpec;
    compare: boolean;
  } | null;
  /* * check accuracy: null = ensure closed; a spec = ensure open in this state */
  checkAccuracy?: {
    prop: string;
    holdout: boolean;
    mistakes: boolean;
    panel: boolean;
  } | null;
  /* * sidebar reveals so the anchor rows exist: folder keys to open + a search override */
  sidebar?: { openGroups?: string[]; query?: string };
  /*
   * the Rx customize session (steps 11+): null = ensure closed; a spec = ensure open on this
   * preset, with exactly this collapsible expanded and the traditional comparison in this state
   */
  rxEditor?: {
    presetId: string;
    /* * the ONE open collapsible ('genius' | 'rates' | …); null = all closed; absent = hands off */
    section?: string | null;
    /* * the Compare-with-Traditional split: true = ensure open, false = ensure closed */
    compareTraditional?: boolean;
    /* * the Compare strip toggle, once the comparison exists */
    compareOn?: boolean;
    /* * the picked cause (2 = diffusion, 4 = clay fixation); once the comparison exists */
    rxCat?: number | null;
  } | null;
  /* * the dashboard Spread Fertilizer flow: panel open, `editing` = the preset id in the editor */
  spread?: { editing: string | null } | null;
}

export interface AnchorRect {
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

export interface WizardStep {
  id: string;
  /* * null = a custom step the host renders itself (the lesson-plan loader) */
  scene: WizardScene | null;
  /*
   * find the element OR viewport rect the widget sits next to (re-resolved continuously —
   * anchors mount late and map anchors move with every pan)
   */
  anchor?: () => HTMLElement | AnchorRect | null;
  /*
   * which side of the anchor the widget sits on ('over' = centered on it); absent = auto by
   * screen half (the original behaviour)
   */
  side?: "left" | "right" | "over";
  highlight?: () => HTMLElement | null;
  /* * the finale card: bigger, centered, with the robot's encore (step 19) */
  finale?: boolean;
  text: (ctx: { fieldName: string }) => string;
  /*
   *
   * passthrough: let the click do its normal platform work too (the next scene builds on it)
   * false: swallow it — the next step's scene realization produces the intended state without the
   * side effect.
   */
  advance?: { match: (el: HTMLElement) => boolean; passthrough: boolean };
  /* * a side effect that runs when the step advances, by D-click or Next (step 16 saves the Rx) */
  onAdvance?: () => void;
  /* * center the widget on screen (the finale has no anchor) */
  center?: boolean;
  /* * whether the widget offers a Next button (the last step offers only X) */
  next: boolean;
}

export interface WizardDef {
  id: string;
  steps: WizardStep[];
}

export interface WizardState {
  wizard: WizardDef | null;
  step: number;
  fieldId: string | null;
  /* * a start-gate message ("select a field first") shown without any wizard running */
  notice: string | null;
  /* * named imperative actions components expose while mounted (see registerAction) */
  actions: Map<string, (arg?: unknown) => void>;
  /* * named RECT providers (see AnchorRect) — polled by the host, so no version is needed */
  rects: Map<string, () => AnchorRect | null>;
  /* * bumped on every register/unregister so the driver re-runs and can invoke newly-mounted actions */
  actionsVersion: number;
  start: (wizard: WizardDef, fieldId: string) => void;
  goto: (step: number) => void;
  next: () => void;
  exit: () => void;
  setNotice: (n: string | null) => void;
  /* * register a named action for the driver/steps to invoke; returns the unregister fn */
  registerAction: (name: string, fn: (arg?: unknown) => void) => () => void;
  /* * register a named anchor-rect provider; returns the unregister fn */
  registerRect: (name: string, fn: () => AnchorRect | null) => () => void;
  /* * invoke a registered action if present; false when its component isn't mounted yet */
  invokeAction: (name: string, arg?: unknown) => boolean;
}

export const useWizard = create<WizardState>((set, get) => ({
  wizard: null,
  step: 0,
  fieldId: null,
  notice: null,
  actions: new Map(),
  rects: new Map(),
  actionsVersion: 0,
  start: (wizard, fieldId) => set({ wizard, fieldId, step: 0, notice: null }),
  goto: (step) => set({ step }),
  next: () =>
    set((s) => {
      if (!s.wizard) return s;
      const last = s.wizard.steps.length - 1;
      return s.step >= last
        ? { wizard: null, fieldId: null, step: 0 }
        : { step: s.step + 1 };
    }),
  exit: () => set({ wizard: null, fieldId: null, step: 0, notice: null }),
  setNotice: (notice) => set({ notice }),
  registerRect: (name, fn) => {
    const m = new Map(get().rects);
    m.set(name, fn);
    set({ rects: m });
    return () => {
      const cur = get().rects;
      if (cur.get(name) !== fn) return;
      const m2 = new Map(cur);
      m2.delete(name);
      set({ rects: m2 });
    };
  },
  registerAction: (name, fn) => {
    const m = new Map(get().actions);
    m.set(name, fn);
    set((s) => ({ actions: m, actionsVersion: s.actionsVersion + 1 }));
    return () => {
      const cur = get().actions;
      if (cur.get(name) !== fn) return; // a newer registration already replaced this one
      const m2 = new Map(cur);
      m2.delete(name);
      set((s) => ({ actions: m2, actionsVersion: s.actionsVersion + 1 }));
    };
  },
  invokeAction: (name, arg) => {
    const fn = get().actions.get(name);
    if (!fn) return false;
    fn(arg);
    return true;
  },
}));

/* * the current step's scene (null when no wizard runs or the step is custom) — the driver's input */
export const sceneOf = (s: WizardState): WizardScene | null =>
  s.wizard?.steps[s.step]?.scene ?? null;

/* * a step anchor that reads a registered rect provider (map-projected anchors) */
export const rectAnchor = (name: string) => (): AnchorRect | null =>
  useWizard.getState().rects.get(name)?.() ?? null;
